import { ButtonProps, Popconfirm, Popover, message } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';

import AntdIcon from '@ant-design/icons';
import { useMappedEventLimit } from '@components/common/RulesBuilder/MappedEventBuilder/api';
import { useDimensionPanelStore } from '@components/dimensions/store';
import EventNameBuilder from '@components/mapping-panel/components/EventNameBuilder';
import RuleBuilder from '@components/mapping-panel/components/RuleBuilder';
import { trimMappedEventRules } from '@components/mapping-panel/utils/trimUtils';
import CancelSvg from '@icons/emp/cancel.svg';
import InfoIcon from '@icons/emp/info.svg';
import MappingRowSvg from '@icons/emp/mapping-builder-lined.svg';
import SaveSvg from '@icons/emp/save.svg';
import {
  useDeleteMappedEvent,
  useGetMappedEvents,
  usePostMappedEvents,
  useUpdateMappedEvent,
} from '@src/api/emp/queryHooks';
import { MappedEventTypes } from '@src/common/types/empTypes';
import { Button } from '@src/emp-3.0/components/common/AntdEnhancement';
import { SEMANTIC_MAPPER, trackMapNewEvent, trackUpdateMappedEvent } from '@src/sensor';
import PermissionButton from '@src/emp-3.0/components/PermissionButton';
import { EInputType } from './components/RuleBuilder/KeyValueBuilder/typed';
import {
  EXISTING_RULES_NOTIFICATION,
  EXISTING_RULES_NOTIFICATION_EDITED,
  IItemActiveIndices,
  IMetadataItemActiveIndices,
  useMappingPanelStore,
} from './store';
import {
  ButtonGroup,
  EventLimitToolTips,
  Holder,
  MappingRulesContainer,
  RuleContainer,
  Title,
  TopBar,
  Wrapper,
} from './styled';
import { EMappingRuleType, ERuleActionType } from './typed';
import {
  dragRulesChange,
  rulesChange,
  transformRulesToState,
  transformStateToRules,
  updateRules,
} from './utils/handleRulesUtils';
import { getEmptyRuleGroup, getInitialActiveIndices } from './utils/initializeUtils';
import { validateMappedEventData } from './utils/validateUtils';
import { isDeployed, hasRelatedFlows, hasRelatedMetrics } from '@src/emp-3.0/utils/checks';
import {
  useCheckDuplicateMappedEvent,
  useUpdateActivateMappedEvent,
} from '@src/emp-3.0/api/queryHooks';
import {
  renderDeployedStatusDeletePrompt,
  renderHasRelatedDeletePrompt,
  renderRelatedList,
} from '@src/emp-3.0/components/Management/components/CellRenderers/ActionConfirmButton';
import { addIsManualFieldToMappedEvent } from '@components/mapping-panel/store/sensorStore';
import { DuplicatePopupContent } from '@src/emp-3.0/components/MappedEventBuilder/DuplicatePopupContent.tsx';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';

const MappingPanel: React.FC = () => {
  const [remainingCount, setRemainingCount] = useState<number>(Infinity);
  const [eventLimitCount, setEventLimitCount] = useState<number>(Infinity);
  const [showRemainingCount, setShowRemainingCount] = useState(false);
  const [duplicateMappedEventName, setDuplicateMappedEventName] = useState<string>('');
  const [eventNameBuilderValidity, setEventNameBuilderValidity] = useState<{ [key: string]: any }>({
    mismatch: false,
    illegal: false,
    repeated: false,
  });

  const [
    mappedEvent,
    predefinedMappedEvent,
    mappingRules,
    activeIndices,
    eventCategory,
    mappedName,
    predefinedMappingRules,
    predefineTitle,
    setMappingRules,
    setActiveIndices,
    setMappingPanelOpen,
    setEventCategory,
    setMappedName,
    setPredefinedMappingRules,
    setPredefinedMappedEvent,
    setMappedEvent,
    setExistingRulesNotification,
  ] = useMappingPanelStore(
    (state) => [
      state.mappedEvent,
      state.predefinedMappedEvent,
      state.mappingRules,
      state.activeIndices,
      state.eventCategory,
      state.mappedName,
      state.predefinedMappingRules,
      state.existingRulesNotification,
      state.setMappingRules,
      state.setActiveIndices,
      state.setMappingPanelOpen,
      state.setEventCategory,
      state.setMappedName,
      state.setPredefinedMappingRules,
      state.setPredefinedMappedEvent,
      state.setMappedEvent,
      state.setExistingRulesNotification,
    ],
    shallow,
  );
  const [setCurPresetName] = useDimensionPanelStore((state) => [state.setCurPresetName]);
  const { workbenchMode } = useInitialInfoStore();

  const { mutate: create, isLoading: createMappedEventLoading } = usePostMappedEvents();
  const { mutate: update, isLoading: updateMappedEventLoading } = useUpdateMappedEvent();
  const { mutate: updateActivateMappedEvent, isLoading: updateActivateMappedEventLoading } =
    useUpdateActivateMappedEvent();
  const { mutate: deleteMappedEvent, isLoading: deleteMappedEventLoading } = useDeleteMappedEvent();
  const { data: mappedEventLimitData, isFetching: eventLimitLoading } = useMappedEventLimit();
  const predefineUnsavedChanges = useRef<Map<string, MappedEventTypes>>(new Map());
  const { data: allMappedEvents } = useGetMappedEvents();

  const { mutate: checkDuplicateMappedEvent, isLoading: isDuplicateCheckLoading } =
    useCheckDuplicateMappedEvent();

  useEffect(() => {
    if (mappedEventLimitData && !eventLimitLoading) {
      const remainingCount = mappedEventLimitData.data.remain_count;
      const eventLimitCount = mappedEventLimitData.data.total_count;
      setRemainingCount(remainingCount);
      setEventLimitCount(eventLimitCount);
      setShowRemainingCount(true);
      if (remainingCount > 0) {
        setTimeout(() => {
          setShowRemainingCount(false);
        }, 3000);
      }
    }
  }, [mappedEventLimitData, eventLimitLoading]);

  useEffect(() => {
    if (mappedEvent?.event_id) {
      const updatedMappedEvent = (allMappedEvents?.data ?? []).find(
        (item) => item.event_id === mappedEvent.event_id,
      );
      updatedMappedEvent && setMappedEvent(updatedMappedEvent);
    }
  }, [allMappedEvents]);

  const isAllMetadataRulesEmpty = useMemo(() => {
    const rulesToCheck = (mappingRules || []).concat(predefinedMappingRules || []);

    if (rulesToCheck.length > 0) {
      return rulesToCheck.some((event) => {
        return (
          event?.metadata &&
          event.metadata.length > 0 &&
          event.metadata.some((andRules) => {
            return andRules?.every((rule) => rule?.key && rule?.value?.length === 0);
          })
        );
      });
    }

    return false;
  }, [mappingRules, predefinedMappingRules]);

  const { mismatch, illegal, repeated } = eventNameBuilderValidity;
  const isUpdateMode = !!mappedEvent?.event_id;
  const eventId = mappedEvent?.event_id;
  const isHasRelatedMetrics = hasRelatedMetrics(mappedEvent);
  const isHasRelatedFlows = hasRelatedFlows(mappedEvent);
  const isHasRelated = isHasRelatedMetrics || isHasRelatedFlows;
  const isPredefineMode = !!predefinedMappedEvent?.event_id;
  const isDeployedMappedEvent = isDeployed(mappedEvent);
  const canDelete = !isHasRelated;
  const canSaveEvent =
    (remainingCount > 0 || isUpdateMode || isPredefineMode) &&
    !eventLimitLoading &&
    !illegal &&
    !repeated &&
    !isAllMetadataRulesEmpty;

  useEffect(() => {
    setDuplicateMappedEventName('');
  }, [mappingRules, predefinedMappingRules]);

  const handleRulesChange = (
    ruleType: EMappingRuleType,
    actionType: ERuleActionType,
    orIndex = 0,
    subAndIndex = 0,
    subOrIndex?: number,
    metadataKey?: string,
  ) => {
    const rules = ruleType === EMappingRuleType.PREDEFINE ? predefinedMappingRules : mappingRules;
    const updatedRules = rulesChange(
      rules,
      actionType,
      orIndex,
      subAndIndex,
      subOrIndex,
      metadataKey,
    );
    ruleType === EMappingRuleType.PREDEFINE
      ? setPredefinedMappingRules(updatedRules)
      : setMappingRules(updatedRules);
  };

  const handleChange = (
    ruleType: EMappingRuleType,
    orIndex: number,
    subAndIndex: number,
    subOrIndex: number,
    type: EInputType,
    value: string,
  ) => {
    if (ruleType === EMappingRuleType.NORMAL) {
      setMappingRules([
        ...updateRules(mappingRules, orIndex, subAndIndex, subOrIndex, type, value),
      ]);
      return;
    }
    if (ruleType === EMappingRuleType.PREDEFINE) {
      setPredefinedMappingRules([
        ...updateRules(predefinedMappingRules, orIndex, subAndIndex, subOrIndex, type, value),
      ]);
      return;
    }
  };

  const updateActiveIndices = useCallback(
    (
      ruleType: EMappingRuleType,
      orIndex?: number,
      eventName?: boolean,
      ruleItems?: IItemActiveIndices[],
      metadataName?: boolean,
      metadataItems?: IMetadataItemActiveIndices[],
    ) => {
      setActiveIndices({ ruleType, orIndex, eventName, metadataName, ruleItems, metadataItems });
    },
    [],
  );

  const handleReset = () => {
    setMappedName(undefined);
    setEventCategory(undefined);
    setMappingRules([getEmptyRuleGroup()]);
    setActiveIndices(getInitialActiveIndices());
    setMappedEvent(undefined);
    setPredefinedMappedEvent(undefined);
  };

  const createMappedEvent = (res: any) => {
    create(res.mappedEvent, {
      onSuccess: () => {
        message.success('create success!');
        handleReset();
        setMappingPanelOpen(false);
        // report to conviva sensor
        const mappedEventWithIsManual = addIsManualFieldToMappedEvent(res.mappedEvent);
        trackMapNewEvent({
          event_name: res.mappedEvent.event_name,
          event_category: res.mappedEvent.event_category,
          type: SEMANTIC_MAPPER,
          isManual: mappedEventWithIsManual.isManual,
          mappedEventWithIsManual: mappedEventWithIsManual,
        });
        /**
         * The current EventCategory does not support manual input at the moment. So there is no need to re-request.
         */
        // refetchEventCategory();
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.msg);
      },
    });
  };

  const updateMappedEvent = (res: any) => {
    const updateHandler = (updateFn, payload: any) => {
      updateFn(payload, {
        onSuccess: () => {
          // report to conviva sensor
          const mappedEventWithIsManual = addIsManualFieldToMappedEvent(res.mappedEvent);
          trackUpdateMappedEvent({
            event_name: res.mappedEvent.event_name,
            event_category: res.mappedEvent.event_category,
            isManual: mappedEventWithIsManual.isManual,
            mappedEventWithIsManual: mappedEventWithIsManual,
          });
          message.success('update success!');
          handleReset();
          setMappingPanelOpen(false);
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.msg);
        },
      });
    };

    updateHandler(update, {
      id: res.mappedEvent.event_id,
      mappedEvent: res.mappedEvent,
    });
  };

  const handleDragEnd = (result, ruleType: EMappingRuleType) => {
    try {
      const rules = ruleType === EMappingRuleType.PREDEFINE ? predefinedMappingRules : mappingRules;
      const res = dragRulesChange(result, ruleType, rules);
      if (res) {
        const { rules, activeIndices } = res;
        if (ruleType === EMappingRuleType.PREDEFINE) {
          setPredefinedMappingRules([...rules]);
        } else {
          setMappingRules([...rules]);
        }
        setActiveIndices(activeIndices);
      }
    } catch (e) {
      console.log('Drag drop end error: ', e);
    }
  };

  const handleSave = (ignoreDuplicateCheck = false) => {
    const normalRules = transformStateToRules(mappingRules);
    const predefinedRules = transformStateToRules(predefinedMappingRules);
    let finalMappedEvent = mappedEvent;
    if (!isUpdateMode && isPredefineMode) {
      finalMappedEvent = predefinedMappedEvent;
    }
    const uncheckedMappedEvent = {
      ...finalMappedEvent,
      event_name: mappedName,
      event_category: eventCategory,
      rules: trimMappedEventRules([[...normalRules, ...predefinedRules]]),
    };

    const res = validateMappedEventData(uncheckedMappedEvent as MappedEventTypes, false);

    if (!res.status) {
      message.warning(`${res.key ? res.key.join(' OR ') : ''} ${res.result}`).then();
      return;
    }
    if (ignoreDuplicateCheck) {
      isUpdateMode || isPredefineMode ? updateMappedEvent(res) : createMappedEvent(res);
      return;
    }
    checkDuplicateMappedEvent(
      { ...res.mappedEvent },
      {
        onSuccess: (response) => {
          let duplicateMappedEvent =
            response.data.filter((dup) => dup.id !== eventId)[0]?.event_name || '';
          if (!duplicateMappedEvent) {
            isUpdateMode || isPredefineMode ? updateMappedEvent(res) : createMappedEvent(res);
          }
          setDuplicateMappedEventName(duplicateMappedEvent);
        },
        onError: (response) => {
          message.error('Failed To Check Duplicate Mapped Event');
          isUpdateMode || isPredefineMode ? updateMappedEvent(res) : createMappedEvent(res);
        },
      },
    );
  };

  const handleCancel = () => {
    handleReset();
    setMappingPanelOpen(false);
    setCurPresetName('Explore');
  };

  const handleEventCategoryChange = (eventCategory: string) => {
    setEventCategory(eventCategory);
  };

  const handleMappedNameChange = (mappedName: string) => {
    setMappedName(mappedName);
    if (predefinedMappedEvent) {
      const latestPredefinedMappedEvent = { ...predefinedMappedEvent };
      latestPredefinedMappedEvent.rules[0] = transformStateToRules(predefinedMappingRules);
      predefineUnsavedChanges.current.set(
        predefinedMappedEvent.event_name,
        latestPredefinedMappedEvent,
      );
    }
  };

  const handlePredefinedMappingRulesChange = (
    predefinedMappedEvent: MappedEventTypes | undefined,
    status?: 'edited' | 'not edited',
  ) => {
    setPredefinedMappedEvent(predefinedMappedEvent);
    setPredefinedMappingRules(transformRulesToState(predefinedMappedEvent?.rules));
    setActiveIndices({ ruleType: EMappingRuleType.PREDEFINE, orIndex: 0 });
    if (status === 'edited') {
      setExistingRulesNotification(EXISTING_RULES_NOTIFICATION_EDITED);
      return;
    }
    setExistingRulesNotification(EXISTING_RULES_NOTIFICATION);
  };

  const handleEventNameBuilderValidityChange = (value) => {
    setEventNameBuilderValidity(value);
  };

  const getDeleteConfirmTitle = () => {
    if (!canDelete) {
      return (
        <div>
          {renderHasRelatedDeletePrompt()}
          {renderRelatedList(mappedEvent)}
        </div>
      );
    }
    return <div style={{ whiteSpace: 'nowrap' }}>{renderDeployedStatusDeletePrompt()}</div>;
  };

  const handleDeleteMappedEvent = () => {
    if (mappedEvent?.event_id) {
      deleteMappedEvent(mappedEvent.event_id, {
        onSuccess: () => {
          handleReset();
          setMappingPanelOpen(false);
          message.success('Delete success!');
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.msg);
        },
      });
    }
  };

  const RemainingTooltip = () => {
    if (isUpdateMode || isPredefineMode) return null;
    if (remainingCount > 0) {
      return (
        <EventLimitToolTips>
          <AntdIcon style={{ fontSize: '16px', marginRight: '10px' }} component={InfoIcon} />
          {`Remaining ${remainingCount} events can be created`}
        </EventLimitToolTips>
      );
    } else {
      return (
        <EventLimitToolTips type="error">
          {`Unable to create, the limit(${eventLimitCount}) has been reached`}
        </EventLimitToolTips>
      );
    }
  };

  const getSaveButton = () => {
    if (workbenchMode) {
      if (!mappedEvent?.is_beta && isUpdateMode) return null;
    }

    const SaveButton: React.FC<ButtonProps> = (props) => (
      <PermissionButton tooltip="">
        <Popover
          content={duplicatePopupContent}
          trigger="click"
          open={!!duplicateMappedEventName}
          onOpenChange={(v) => {
            !v && setDuplicateMappedEventName('');
          }}
        >
          <Button
            className={
              !canSaveEvent || duplicateMappedEventName !== '' ? 'disabled save-btn' : 'save-btn'
            }
            icon={<AntdIcon component={SaveSvg} style={{ fontSize: '14px' }} />}
            loading={
              eventLimitLoading ||
              createMappedEventLoading ||
              updateMappedEventLoading ||
              isDuplicateCheckLoading ||
              updateActivateMappedEventLoading
            }
            disabled={!canSaveEvent || duplicateMappedEventName !== ''}
            {...props}
          >
            Save
          </Button>
        </Popover>
      </PermissionButton>
    );

    let title: any = '';
    if (mismatch) {
      title = (
        <div style={{ width: '336px' }}>
          The pre-defined Mapped Name does not match the Event Category. Please modify them
          accordingly.
        </div>
      );
      return (
        <Popconfirm
          key="Save Mapping Rules"
          title={title}
          okText="OK"
          okButtonProps={{
            type: 'ghost',
            style: {
              border: '1px solid #0046ff',
              color: '#0046ff',
            },
          }}
          showCancel={false}
          icon={null}
        >
          <SaveButton />
        </Popconfirm>
      );
    }
    if (isHasRelated || isDeployedMappedEvent) {
      if (isHasRelated && isDeployedMappedEvent) {
        title = (
          <div>
            <div style={{ width: '336px' }}>
              Modifying this event will affect related metrics/flows. Also, this is a released
              event, the modification will take effect after deploying the product. Do you want to
              continue?
            </div>
            {renderRelatedList(mappedEvent)}
          </div>
        );
      } else {
        if (isDeployedMappedEvent) {
          title = (
            <div style={{ width: '336px' }}>
              This is a released event, the modification will replace existing one when deployed. Do
              you want to continue?
            </div>
          );
        }
        if (isHasRelated) {
          title = (
            <div>
              <div style={{ width: '336px' }}>
                Modifying this event will affect related metrics/flows. Do you want to continue?
              </div>
              {renderRelatedList(mappedEvent)}
            </div>
          );
        }
      }
    }
    if (title !== '') {
      return (
        <Popconfirm
          key="Save Mapping Rules"
          title={title}
          okText="Save"
          okButtonProps={{
            type: 'ghost',
            style: {
              border: '1px solid #0046ff',
              color: '#0046ff',
            },
          }}
          icon={null}
          onConfirm={() => {
            handleSave(false);
          }}
        >
          <SaveButton />
        </Popconfirm>
      );
    }
    return (
      <SaveButton
        onClick={() => {
          handleSave(false);
        }}
      />
    );
  };

  const getDeleteButton = () => {
    {
      /* 
            update = false -> node show
            workbench = true && workbenchAsset = true && update -> show
            workbench = true && workbenchAsset = false && update -> not show
            workbench = false && update -> show */
    }
    if (workbenchMode && !mappedEvent?.is_beta) {
      return null;
    }
    if (!isUpdateMode) return null;
    return (
      <>
        <PermissionButton
          tooltip=""
          styleType="default"
          onConfirm={() => {
            if (canDelete) {
              handleDeleteMappedEvent();
            }
          }}
        >
          <Popconfirm
            key="Delete Mapping Rules"
            title={getDeleteConfirmTitle()}
            showCancel={canDelete}
            okText={canDelete ? (isDeployedMappedEvent ? 'Undeploy & Delete' : 'Delete') : 'Got it'}
            cancelButtonProps={{ type: 'default' }}
            okButtonProps={{
              danger: canDelete,
              type: 'ghost',
              style: canDelete
                ? {
                    border: '1px solid #d0021b',
                    color: '#d0021b',
                  }
                : {
                    border: '1px solid #0046ff',
                    color: '#0046ff',
                  },
            }}
            icon={false}
          >
            <Button className="delete-btn" danger loading={deleteMappedEventLoading}>
              Delete
            </Button>
          </Popconfirm>
        </PermissionButton>
        <div
          style={{
            width: '1px',
            height: '24px',
            background: '#D9D9D9',
            margin: '0 7px',
          }}
        />
      </>
    );
  };

  const duplicatePopupContent = (
    <DuplicatePopupContent
      duplicateMappedEventName={duplicateMappedEventName}
      footer={
        <>
          <Button
            onClick={() => {
              setDuplicateMappedEventName('');
            }}
          >
            Edit This Rule
          </Button>
        </>
      }
    ></DuplicatePopupContent>
  );
  return (
    <Wrapper>
      <TopBar>
        <Title>
          <AntdIcon component={MappingRowSvg} style={{ fontSize: '20px', fontWeight: 400 }} />
          <span>Semantic Mapper</span>
        </Title>
        <ButtonGroup>
          {showRemainingCount && <RemainingTooltip />}
          {getDeleteButton()}
          {getSaveButton()}
          <Button
            key="Cancel Mapping Rules"
            className="cancel-btn"
            onClick={handleCancel}
            icon={<AntdIcon component={CancelSvg} style={{ fontSize: '13px' }} />}
          >
            Cancel
          </Button>
        </ButtonGroup>
      </TopBar>
      <RuleContainer>
        <MappingRulesContainer>
          <RuleBuilder
            title="Set Mapping Rules"
            data={mappingRules}
            activeIndices={
              activeIndices.ruleType === EMappingRuleType.NORMAL ? activeIndices : undefined
            }
            onChange={(...rest) => handleChange(EMappingRuleType.NORMAL, ...rest)}
            updateActiveIndices={(...rest) => updateActiveIndices(EMappingRuleType.NORMAL, ...rest)}
            onRulesChange={(...rest) => handleRulesChange(EMappingRuleType.NORMAL, ...rest)}
            onDragEnd={(result) => handleDragEnd(result, EMappingRuleType.NORMAL)}
          />
          {predefinedMappedEvent ? (
            <>
              <Holder />
              <RuleBuilder
                title={predefineTitle}
                data={predefinedMappingRules}
                showAddNewEventRuleBtn={false}
                activeIndices={
                  activeIndices.ruleType === EMappingRuleType.PREDEFINE ? activeIndices : undefined
                }
                onChange={(...rest) => handleChange(EMappingRuleType.PREDEFINE, ...rest)}
                updateActiveIndices={(...rest) =>
                  updateActiveIndices(EMappingRuleType.PREDEFINE, ...rest)
                }
                onRulesChange={(...rest) => handleRulesChange(EMappingRuleType.PREDEFINE, ...rest)}
                onDragEnd={(result) => handleDragEnd(result, EMappingRuleType.PREDEFINE)}
              />
            </>
          ) : null}
        </MappingRulesContainer>
        <EventNameBuilder
          mappedEvent={mappedEvent}
          eventCategory={eventCategory}
          mappedName={mappedName}
          onChange={handleEventNameBuilderValidityChange}
          onEventCategoryChange={handleEventCategoryChange}
          onMappedNameChange={handleMappedNameChange}
          onPredefinedMappingRulesChange={handlePredefinedMappingRulesChange}
          predefineUnsavedChanges={predefineUnsavedChanges.current}
        />
      </RuleContainer>
    </Wrapper>
  );
};

export default React.memo(MappingPanel);
